import { useIntl } from '@leagueplatform/locales';
import type { UserMenuSection } from '@leagueplatform/solutions-foundation';
import { useGetUserProfile } from '@leagueplatform/web-common';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { EXTERNAL_LINKS } from 'common/links';
import { noop } from 'common/noop';

type Options = {
  onDeactivateClick: () => void;
};

export const useUserMenuConfig = ({
  onDeactivateClick,
}: Options): { userMenu: UserMenuSection[] } => {
  const { formatMessage } = useIntl();
  const { data } = useGetUserProfile();

  // Switch Accounts only applies to members enrolled in more than one plan.
  // plan_count is a Geisinger user-profile custom field carrying a string
  // count; a missing or unparseable value is NaN (not > 1), so the row stays
  // hidden until a real multi-plan signal arrives.
  const planCount = Number(
    data?.customFields?.find((field) => field.name === 'plan_count')?.value,
  );
  const hasMultiplePlans = planCount > 1;

  const accountItems = [
    ...(hasMultiplePlans
      ? [
          {
            label: formatMessage({ id: 'STR_SWITCH_ACCOUNTS' }),
            to: APP_MANAGED_ROUTES.SWITCH_ACCOUNTS,
          },
        ]
      : []),
    {
      label: formatMessage({ id: 'STR_PERSONAL_INFO' }),
      to: APP_MANAGED_ROUTES.PERSONAL_INFO,
    },
    {
      label: formatMessage({ id: 'STR_COMMUNICATION_PREFERENCES' }),
      to: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES,
    },
    {
      label: formatMessage({ id: 'STR_NOTIFICATIONS' }),
      to: APP_MANAGED_ROUTES.NOTIFICATIONS,
    },
    {
      label: formatMessage({ id: 'STR_SHARING_YOUR_DATA' }),
      // TODO: wire external destination (URL pending from PM)
      onClick: noop,
    },
    {
      label: formatMessage({ id: 'STR_SECURITY_PREFERENCES' }),
      // TODO: wire external destination (URL pending from PM)
      onClick: noop,
    },
  ];

  const userMenu: UserMenuSection[] = [
    {
      heading: formatMessage({ id: 'STR_ACCOUNT' }),
      items: accountItems,
    },
    {
      heading: formatMessage({ id: 'STR_ABOUT' }),
      items: [
        {
          label: formatMessage({ id: 'STR_PRIVACY_POLICY' }),
          to: EXTERNAL_LINKS.PRIVACY,
          target: '_blank',
        },
        {
          label: formatMessage({ id: 'STR_TERMS_OF_SERVICE' }),
          to: EXTERNAL_LINKS.TERMS,
          target: '_blank',
        },
        {
          label: formatMessage({ id: 'STR_DEACTIVATE_PORTAL_PROFILE' }),
          onClick: onDeactivateClick,
        },
      ],
    },
    {
      items: [
        {
          label: formatMessage({ id: 'STR_SIGN_OUT' }),
          to: APP_MANAGED_ROUTES.SIGN_OUT,
          variant: 'critical',
        },
      ],
    },
  ];

  return { userMenu };
};
